import type { NxCell } from "@qlik/api/qix";
import type { useConstraints, useSelections, useTheme } from "@nebula.js/stardust";
import { Network } from "vis-network";
import type { Data, Edge, Node, Options } from "vis-network";
import type { SnNetworkChartLayout } from "../tsp-output/types";
import defaults from "../tsp-output/defaults.json";
import { createTooltipHTML } from "./tooltip";
import { escapeHTML } from "./utilities";

type ResolvedLayout = SnNetworkChartLayout &
  Required<
    Pick<
      SnNetworkChartLayout,
      | "showTitles"
      | "title"
      | "subtitle"
      | "footnote"
      | "edgeType"
      | "displayEdgeLabel"
      | "posEdgeLabel"
      | "nodeShape"
      | "shadowMode"
    >
  >;

type PaintTheme = ReturnType<typeof useTheme>;
type PaintConstraints = ReturnType<typeof useConstraints>;
type PaintSelections = ReturnType<typeof useSelections>;

interface PaintInput {
  element: HTMLElement;
  layout: SnNetworkChartLayout;
  theme: PaintTheme;
  selections: PaintSelections;
  constraints: PaintConstraints;
}

interface NetworkDatum {
  id: string;
  eNum?: number;
  label: string;
  parentid?: string;
  group?: string;
  title?: string | HTMLElement;
  nodeValue?: number;
  edgeValue?: number;
}

interface ChartNode extends Node {
  eNum?: number;
}

export function resolveLayoutDefaults(layout: SnNetworkChartLayout): ResolvedLayout {
  return {
    ...layout,
    showTitles: layout.showTitles ?? defaults.showTitles,
    title: layout.title ?? defaults.title,
    subtitle: layout.subtitle ?? defaults.subtitle,
    footnote: layout.footnote ?? defaults.footnote,
    edgeType: (layout.edgeType ?? defaults.edgeType) as NonNullable<SnNetworkChartLayout["edgeType"]>,
    displayEdgeLabel: layout.displayEdgeLabel ?? defaults.displayEdgeLabel,
    posEdgeLabel: (layout.posEdgeLabel ?? defaults.posEdgeLabel) as NonNullable<SnNetworkChartLayout["posEdgeLabel"]>,
    nodeShape: (layout.nodeShape ?? defaults.nodeShape) as NonNullable<SnNetworkChartLayout["nodeShape"]>,
    shadowMode: layout.shadowMode ?? defaults.shadowMode,
  };
}

function isTextCellNotEmpty(cell: NxCell | undefined): cell is NxCell & { qText: string } {
  return Boolean(cell?.qText && !(cell.qIsNull || cell.qText.trim() === ""));
}

function getColor(index: number, colors: string[]): string | undefined {
  return colors.length > 0 ? colors[index % colors.length] : undefined;
}

export default function paint({
  element,
  layout: inputLayout,
  theme,
  selections,
  constraints,
}: PaintInput): Promise<Network | undefined> {
  const layout = resolveLayoutDefaults(inputLayout);

  return new Promise((resolve) => {
    const colorScale = theme.getDataColorPalettes()[0];
    const numDimensions = layout.qHyperCube.qDimensionInfo?.length ?? 0;
    const numMeasures = layout.qHyperCube.qMeasureInfo?.length ?? 0;
    const matrix = layout.qHyperCube.qDataPages?.[0]?.qMatrix;

    if (!matrix) {
      resolve(undefined);
      return;
    }

    const containerId = `network-container_${layout.qInfo?.qId ?? "chart"}`;
    const chartElement = element;
    chartElement.textContent = "";
    const topDiv = document.createElement("div");
    topDiv.id = containerId;
    topDiv.classList.add("sn-network-top");
    if (constraints.passive) topDiv.classList.add("is-edit-mode");
    chartElement.append(topDiv);

    const dataSet: NetworkDatum[] = matrix.flatMap((row: NxCell[]) => {
      const id = row[0]?.qText;
      const nodeName = row[1]?.qText;
      const parentid = row[2]?.qText;
      if (id === undefined || nodeName === undefined) return [];

      const dataItem: NetworkDatum = {
        id,
        eNum: row[0]?.qElemNumber,
        label: nodeName,
      };
      if (parentid !== undefined) dataItem.parentid = parentid;
      let groupNumber: string | undefined;

      if (numDimensions === 4) {
        groupNumber = row[3]?.qText;
        if (groupNumber !== undefined) dataItem.group = groupNumber;
      }

      if (numMeasures > 0) {
        const tooltip = row[numDimensions];
        if (isTextCellNotEmpty(tooltip)) {
          dataItem.title = escapeHTML(tooltip.qText);
        } else if (numMeasures > 1) {
          dataItem.title = createTooltipHTML({
            name: nodeName,
            groupNumber,
            nodeMeasure: row[numDimensions + 1]?.qText,
          });
        }
      }

      if (numMeasures > 1) {
        const nodeValue = row[numDimensions + 1]?.qNum;
        if (nodeValue) dataItem.nodeValue = nodeValue;
      }

      if (numMeasures > 2) {
        const edgeValue = row[numDimensions + 2]?.qNum;
        if (edgeValue) dataItem.edgeValue = edgeValue;
      }

      return [dataItem];
    });

    const uniqueIds = new Set<string>();
    const nodes: ChartNode[] = [];
    const edges: Edge[] = [];
    const groups: Record<string, { color?: string }> = {};

    for (const dataItem of dataSet) {
      if (dataItem.parentid !== undefined) {
        if (layout.displayEdgeLabel && dataItem.edgeValue !== undefined) {
          edges.push({
            from: dataItem.id,
            to: dataItem.parentid,
            value: dataItem.edgeValue,
            label: `${dataItem.edgeValue}`,
          });
        } else {
          edges.push({
            from: dataItem.id,
            to: dataItem.parentid,
            value: dataItem.edgeValue,
          });
        }
      }

      if (!uniqueIds.has(dataItem.id)) {
        uniqueIds.add(dataItem.id);
        nodes.push({
          id: dataItem.id,
          eNum: dataItem.eNum,
          label: dataItem.label,
          title: dataItem.title,
          group: dataItem.group,
          value: dataItem.nodeValue,
        });
        groups[dataItem.group ?? ""] ??= {};
      }
    }

    const paletteSets = colorScale.colors;
    const paletteIndex = Math.min(Math.max(Object.keys(groups).length - 1, 0), paletteSets.length - 1);
    const selectedPalette = paletteSets[paletteIndex];
    const colors = typeof selectedPalette === "string" ? [selectedPalette] : selectedPalette;

    Object.keys(groups).forEach((group, index) => {
      const color = getColor(index, colors);
      if (color) groups[group].color = color;
    });

    const data: Data = { nodes, edges };
    const container = document.getElementById(containerId);
    if (!container) {
      resolve(undefined);
      return;
    }

    const options: Options = {
      groups,
      layout: { randomSeed: 34545 },
      nodes: {
        shape: layout.nodeShape,
        shadow: layout.shadowMode,
      },
      edges: {
        shadow: layout.shadowMode,
        font: { align: layout.posEdgeLabel },
        smooth: {
          enabled: true,
          type: layout.edgeType,
          roundness: 0.5,
        },
      },
      interaction: {
        hideEdgesOnDrag: true,
        selectable: !constraints.active && !constraints.select,
        tooltipDelay: 100,
        multiselect: true,
        selectConnectedEdges: true,
      },
      physics: {
        forceAtlas2Based: {
          gravitationalConstant: -100,
          centralGravity: 0.005,
          springLength: 230,
          springConstant: 0.18,
        },
        maxVelocity: 146,
        solver: "forceAtlas2Based",
        timestep: 0.35,
        stabilization: { iterations: 150 },
      },
    };

    const network = new Network(container, data, options);
    network.fit();
    network.on("select", () => {
      if (constraints.active || constraints.select) return;

      const selectedNodes = network.getSelectedNodes();
      if (selectedNodes.length === 0) return;

      const connectedNodes = new Set<string | number>(selectedNodes);
      selectedNodes.forEach((node) => {
        network.getConnectedNodes(node).forEach((connectedNode) => {
          if (typeof connectedNode === "string" || typeof connectedNode === "number") {
            connectedNodes.add(connectedNode);
          }
        });
      });

      const toSelect = new Set<number>();
      connectedNodes.forEach((node) => {
        data.nodes?.forEach((dataNode) => {
          const chartNode = dataNode as ChartNode;
          if (chartNode.id === node && chartNode.eNum !== undefined && node !== "-") {
            toSelect.add(chartNode.eNum);
          }
        });
      });

      if (!selections.isActive()) void selections.begin(["/qHyperCubeDef"]);
      void selections.select({
        method: "selectHyperCubeValues",
        params: ["/qHyperCubeDef", 0, [...toSelect], false],
      });
    });

    network.on("stabilizationIterationsDone", () => {
      network.stopSimulation();
      topDiv.dataset.stabilized = "true";
      resolve(network);
    });
  });
}
